import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  IAuthUser,
  IAuthenticatedSession,
  IJwtPayload,
  ITwoFactorAwaiting,
  ITwoFactorRequired,
} from '@kinet-open-network/shared/data-access/model';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, finalize } from 'rxjs';
import { IChangePassword } from '../models/change-password.interface';
import { IForgotPassword } from '../models/forgot-password.interface';
import { ILogin } from '../models/login.interface';
import { IResetPassword } from '../models/reset-password.interface';
import { ITwoFactorEnable } from '../models/two-factor-enable.interface';
import { ITwoFactorLogin } from '../models/two-factor-login.interface';
import { IUpdateProfile } from '../models/update-profile.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);

  isRefreshing$ = new BehaviorSubject<boolean>(false);

  login(data: ILogin) {
    return this.http.post<{
      data: IAuthenticatedSession | ITwoFactorAwaiting | ITwoFactorRequired;
    }>(`/auth/login`, data);
  }

  twoFactorLogin(data: ITwoFactorLogin) {
    return this.http.post<{ data: IAuthenticatedSession }>(
      `/auth/2fa/login`,
      data,
    );
  }

  refresh(refreshToken: string) {
    this.isRefreshing$.next(true);

    return this.http
      .post<{ data: IAuthenticatedSession }>(
        '/auth/refresh',
        {},
        { headers: { 'x-refresh-token': refreshToken } },
      )
      .pipe(finalize(() => this.isRefreshing$.next(false)));
  }

  logout(refreshToken: string) {
    return this.http.post<void>(
      '/auth/logout',
      {},
      {
        headers: { 'x-refresh-token': refreshToken },
      },
    );
  }

  decodeToken<T extends IJwtPayload>(token: string) {
    return jwtDecode<T>(token);
  }

  getTokenExpiration(token: string) {
    const { exp } = this.decodeToken(token);

    return exp * 1000;
  }

  resetPassword(data: IResetPassword) {
    return this.http.post<{ data: IResetPassword }>(
      `/auth/reset-password`,
      data,
    );
  }

  forgotPassword(data: IForgotPassword) {
    return this.http.post<{ email: string }>('/auth/forgot-password', data);
  }

  changePassword(data: IChangePassword) {
    return this.http.post<{ message: string }>(`/auth/change-password`, data);
  }

  getTwoFactorQrCode(data: ILogin) {
    return this.http.post('/auth/2fa/qr-code', data, { responseType: 'blob' });
  }

  enableTwoFactor(data: ITwoFactorEnable) {
    return this.http.post<{ message: string }>('/auth/2fa/enable', data);
  }

  updateProfile(data: IUpdateProfile) {
    return this.http.put<{ data: IAuthUser; message: string }>(
      '/auth/profile',
      data,
    );
  }
}
